// Sessiebewaking rond window.fetch — één plek voor twee dingen die anders op
// ~120 fetch-aanroepen in App.jsx herhaald zouden moeten worden:
//
//  1. Een VERLENGD token meesturen. De app verlengt een lopende sessie stil
//     (POST /api/auth/refresh, zie AuthContext). Het React-`token` dat overal in
//     closures en useMemo-afhankelijkheden zit, blijft daarbij bewust ONGEWIJZIGD:
//     een nieuwe waarde zou elke `apiFetch` opnieuw laten bouwen en elke
//     data-effect (orders van 46 MB incl.) opnieuw laten laden. In plaats daarvan
//     vertaalt deze wrapper een verouderd Bearer-token naar zijn opvolger vlak
//     vóór het verzoek vertrekt. Alleen tokens die door een verlenging vervangen
//     zijn, worden vertaald — een header met een ander token (impersonatie,
//     overdracht) blijft onaangeroerd.
//
//  2. Een VERLOPEN sessie herkennen. Antwoordt de server 401 met een sessiecode
//     (session_expired / invalid_token / token_required, middleware/auth.js) op
//     een verzoek dat een Bearer droeg, dan wordt `opVerlopen(code, melding)`
//     één keer aangeroepen; AuthContext meldt de gebruiker dan netjes af en toont
//     de reden op het loginscherm. Tot 16-09-2026 bleef de app gewoon staan met
//     de eerder geladen data en gaf elke actie "Ongeldig token".
//
// Alleen verzoeken naar onze eigen API (/api/…, zelfde origin) worden bekeken.

export const SESSIE_CODES = new Set(['session_expired', 'invalid_token', 'token_required']);

function urlVan(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url; // Request
  if (input && typeof input.href === 'string') return input.href; // URL
  return '';
}

export function isEigenApi(url, origin) {
  if (!url) return false;
  if (url.startsWith('/api/')) return true;
  if (origin && url.startsWith(origin + '/api/')) return true;
  return false;
}

function bearerUit(headers) {
  if (!headers) return null;
  let waarde = null;
  if (typeof headers.get === 'function') waarde = headers.get('Authorization') || headers.get('authorization');
  else waarde = headers.Authorization || headers.authorization;
  if (!waarde || typeof waarde !== 'string') return null;
  const m = /^Bearer\s+(.+)$/i.exec(waarde.trim());
  return m ? m[1] : null;
}

function metBearer(headers, token) {
  const nieuw = `Bearer ${token}`;
  if (headers && typeof headers.set === 'function') {
    const kopie = new Headers(headers);
    kopie.set('Authorization', nieuw);
    return kopie;
  }
  const kopie = { ...(headers || {}) };
  if ('authorization' in kopie) kopie.authorization = nieuw; else kopie.Authorization = nieuw;
  return kopie;
}

// Vertaalregister: oud token → opvolger. Een keten (a→b→c) wordt tot het laatste
// token gevolgd, zodat een closure met het allereerste token ook na meerdere
// verlengingen het huidige token stuurt.
export function maakVertaler() {
  const opvolgers = new Map();
  return {
    noteer(oud, nieuw) { if (oud && nieuw && oud !== nieuw) opvolgers.set(oud, nieuw); },
    huidig(token) {
      let t = token; let stappen = 0;
      while (t && opvolgers.has(t) && stappen < 1000) { t = opvolgers.get(t); stappen++; }
      return t;
    },
    wis() { opvolgers.clear(); },
  };
}

// Installeert de wrapper; geeft een functie terug die de oorspronkelijke fetch
// herstelt. `vertaler` = maakVertaler(); `opVerlopen(code, melding)`.
export function installeerSessieFetch({ vertaler, opVerlopen, venster = typeof window !== 'undefined' ? window : null }) {
  const origineel = venster && venster.fetch;
  if (typeof origineel !== 'function') return () => {};
  const origin = venster.location && venster.location.origin;
  let gemeld = false;

  venster.fetch = async function sessieFetch(input, init) {
    const url = urlVan(input);
    if (!isEigenApi(url, origin)) return origineel.call(venster, input, init);

    let opties = init;
    const headersBron = (init && init.headers) || (input && input.headers) || null;
    const oud = bearerUit(headersBron);
    if (oud) {
      const nieuw = vertaler.huidig(oud);
      if (nieuw !== oud) opties = { ...(init || {}), headers: metBearer(headersBron, nieuw) };
    }

    const antwoord = await origineel.call(venster, input, opties);
    if (antwoord && antwoord.status === 401 && oud && !gemeld) {
      let body = null;
      try { body = await antwoord.clone().json(); } catch { /* geen JSON */ }
      const code = body && body.code;
      if (code && SESSIE_CODES.has(code)) {
        gemeld = true;
        try { opVerlopen(code, body.error || ''); } catch { /* nooit het verzoek laten stranden */ }
        // Na een nieuwe aanmelding mag een volgende verlopen sessie opnieuw gemeld worden.
        setTimeout(() => { gemeld = false; }, 5000);
      }
    }
    return antwoord;
  };

  return () => { venster.fetch = origineel; };
}
